import { UserManager, WebStorageStateStore } from 'oidc-client-ts'
import { saveAuthToken } from 'services/auth'
import { factset } from 'config'

let _userManager = null

/**
 * Creates and configures the OIDC UserManager.
 * Uses oidc-client-ts — see https://authts.github.io/oidc-client-ts/classes/UserManager.html
 * FactSet OAuth2 guidelines — see https://github.com/factset/oauth2-guidelines
 * @returns {import('oidc-client-ts').UserManager} The configured UserManager instance.
 */
function createUserManager() {
  const authority = factset.oidcAuthority
  const manager = new UserManager({
    authority,
    client_id: factset.oidcClientId,
    redirect_uri: `${window.location.origin}/callback`,
    post_logout_redirect_uri: window.location.origin,
    response_type: 'code',
    scope: 'openid',
    automaticSilentRenew: false,
    userStore: new WebStorageStateStore({ store: localStorage }),
    metadata: {
      issuer: authority,
      authorization_endpoint: `${authority}/as/authorization.oauth2`,
      token_endpoint: `${authority}/as/token.oauth2`,
      revocation_endpoint: `${authority}/as/revoke_token.oauth2`,
      userinfo_endpoint: `${authority}/idp/userinfo.openid`,
      end_session_endpoint: `${authority}/idp/startSLO.ping`,
      jwks_uri: `${authority}/pf/JWKS`,
      code_challenge_methods_supported: ['S256'],
    },
  })

  // addUserLoaded fires after signinRedirectCallback stores the user.
  // Persist the access token so httpClient can attach it to API requests immediately.
  manager.events.addUserLoaded(user => {
    saveAuthToken(user.access_token)
  })

  // Proactively refresh the access token before it expires using the refresh_token grant.
  // This is a direct POST to the token endpoint — no iframe, no third-party cookies —
  // so it works in Firefox with Enhanced Tracking Protection enabled.
  // On failure we do NOT redirect — the token is still valid (event fires 60s before expiry)
  // and the reactive 401 handler in httpClient will recover on the next request.
  manager.events.addAccessTokenExpiring(() => {
    manager.signinSilent().catch(() => {})
  })

  // addUserUnloaded fires when the user is removed from storage (e.g. token expiry detected
  // by oidc-client-ts internally). Keep it registered as a no-op so the intent is explicit:
  // token cleanup is handled through the explicit logout flow in useAuth.js.
  manager.events.addUserUnloaded(() => {})

  return manager
}

/**
 * Lazily creates and returns the OIDC UserManager singleton.
 * @returns {import('oidc-client-ts').UserManager} The configured UserManager instance.
 */
export function getUserManager() {
  if (!_userManager) {
    _userManager = createUserManager()
  }
  return _userManager
}

/**
 * Returns the stored OIDC user if valid, or triggers a signinRedirect and returns null.
 * Callers should `return` (or suspend) immediately when this returns null.
 * @returns {Promise<import('oidc-client-ts').User|null>} The valid user, or null if a redirect was initiated.
 */
export async function getValidOidcUser() {
  const mgr = getUserManager()
  const storedUser = await mgr.getUser().catch(() => null)

  if (storedUser && !storedUser.expired) {
    return storedUser
  }

  // Token expired — attempt a refresh_token grant before falling back to a full redirect.
  if (storedUser?.refresh_token) {
    const refreshed = await mgr.signinSilent().catch(() => null)
    if (refreshed) {
      return refreshed
    }
  }

  await mgr.signinRedirect({ state: { returnTo: window.location.pathname } })
  return null
}
